// Reusable confirm dialog — replaces native `window.confirm` calls
// with the dashboard's overlay aesthetic. Built on OverlayDialog so
// Escape / backdrop-click dismiss behavior is consistent with every
// other modal in the app. Use cases: delete confirmations, destructive
// edits, anywhere a yes/no with a clear primary action belongs.
//
// Pass `destructive` when the primary action is something the user
// would regret if accidentally clicked — flips the primary button to
// the red treatment so the eye lands on Cancel first.

import OverlayDialog from "./OverlayDialog.jsx";
import { phAttrs } from "../lib/analytics/attrs.js";
import { ACTION, ATTRIBUTE } from "../lib/analytics/surfaceVocabulary.js";

export default function ConfirmDialog({
    open,
    title,
    body,
    eyebrow = "Confirm",
    confirmLabel = "Confirm",
    confirmDisabled = false,
    cancelLabel = "Cancel",
    destructive = false,
    onConfirm,
    onCancel,
    width = "440px",
    className = "",
    analyticsId = null,
}) {
    const primaryClass = destructive
        ? "h-[30px] rounded-[2px] border border-[color:var(--red,#c0392b)] bg-transparent px-[10px] text-[11px] leading-[1.08] tracking-[0.04em] uppercase text-[color:var(--red,#c0392b)] font-medium hover:bg-[color:var(--red,#c0392b)] hover:text-[color:var(--ink)] cursor-pointer"
        : "h-[30px] rounded-[2px] border border-[color:var(--green)] bg-[color:var(--green-soft)] px-[10px] text-[11px] leading-[1.08] tracking-[0.04em] uppercase text-[color:var(--ink)] font-medium hover:border-[color:var(--green)] cursor-pointer";

    return (
        <OverlayDialog
            open={open}
            onClose={onCancel}
            eyebrow={eyebrow}
            title={title}
            width={width}
            className={className}
            analyticsId={analyticsId}
        >
            <div className="grid gap-[14px]">
                {body ? (
                    typeof body === "string" ? (
                        <p className="m-0 text-[12px] leading-[1.45] text-[color:var(--muted)]">{body}</p>
                    ) : (
                        body
                    )
                ) : null}
                <div className="flex items-center justify-end gap-[8px]">
                    <button
                        type="button"
                        onClick={onCancel}
                        data-confirm-dialog-cancel
                        {...phAttrs({ [ATTRIBUTE.ACTION]: ACTION.CANCEL })}
                        className="h-[30px] rounded-[2px] border border-[color:var(--line)] bg-transparent px-[10px] text-[11px] leading-[1.08] tracking-[0.04em] uppercase text-[color:var(--muted-soft)] hover:border-[color:var(--line-strong)] hover:text-[color:var(--ink)] cursor-pointer"
                    >
                        {cancelLabel}
                    </button>
                    <button
                        type="button"
                        onClick={onConfirm}
                        disabled={confirmDisabled}
                        autoFocus
                        data-confirm-dialog-primary
                        {...phAttrs({ [ATTRIBUTE.ACTION]: ACTION.CONFIRM })}
                        className={`${primaryClass} disabled:cursor-not-allowed disabled:opacity-50`}
                    >
                        {confirmLabel}
                    </button>
                </div>
            </div>
        </OverlayDialog>
    );
}
